import { createWithEqualityFn } from "zustand/traditional";
import { createJSONStorage, persist } from "zustand/middleware";

import { isWeb } from "@companion-core/shared/app/Utils/platform";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { APIBlock } from "../Interfaces/API";
import { BlockParams } from "../Interfaces/blocks";

interface StepperBlock {
  block: APIBlock;
  params: BlockParams;
}

interface StepperPageState {
  stepIndex: number;
  blocks: StepperBlock[];
}

interface DynamicPageStepperState {
  pageSlug: string | null;
  pages: Record<string, StepperPageState>;
}

interface DynamicPageStepperActions {
  setStepIndex: (stepIndex: number) => void;
  setBlocks: (blocks: APIBlock[]) => void;
  setPageSlug: (pageSlug: string) => void;
  setBlockParams: (params: BlockParams, stepIndex: number) => void;
  resetPage: (pageSlug?: string) => void;
  reset: () => void;
}

const emptyPageState: StepperPageState = { stepIndex: 0, blocks: [] };

export const initialState: DynamicPageStepperState = {
  pageSlug: null,
  pages: {},
};

/**
 * Persisted store backing the `DynamicPageStepper` (onboarding wizard flow).
 *
 * State is keyed by page slug so that each stepper page keeps its own progress
 * independently: `pages[slug]` holds that page's current `stepIndex` and its
 * `blocks` (each block paired with the `params` collected at that step).
 * `pageSlug` points to the currently active page; all mutating actions
 * (`setStepIndex`, `setBlocks`, `setBlockParams`) operate on that active slug.
 *
 * Actions:
 * - `setPageSlug`   : marks a slug as active, creating an empty entry if needed
 *                     (never overwrites an existing one, so progress is kept).
 * - `setStepIndex`  : sets the current step for the active page.
 * - `setBlocks`     : (re)initializes the active page's blocks from the API,
 *                     resetting their collected params.
 * - `setBlockParams`: stores the params collected for the block at a given step.
 * - `resetPage`     : resets a single page (the active one by default) to step 0.
 * - `reset`         : clears the whole store.
 */
export const useDynamicPageStepperStore = createWithEqualityFn<
  DynamicPageStepperState & DynamicPageStepperActions
>()(
  persist(
    (set, get) => ({
      ...initialState,
      setPageSlug: (pageSlug: string) =>
        set((state) => ({
          pageSlug,
          pages: state.pages[pageSlug]
            ? state.pages
            : { ...state.pages, [pageSlug]: { ...emptyPageState } },
        })),
      setStepIndex: (stepIndex: number) => {
        const { pageSlug } = get();
        if (!pageSlug) return;
        set((state) => ({
          pages: {
            ...state.pages,
            [pageSlug]: { ...(state.pages[pageSlug] ?? emptyPageState), stepIndex },
          },
        }));
      },
      setBlocks: (blocks: APIBlock[]) => {
        const { pageSlug } = get();
        if (!pageSlug) return;
        set((state) => ({
          pages: {
            ...state.pages,
            [pageSlug]: {
              ...(state.pages[pageSlug] ?? emptyPageState),
              blocks: blocks.map((value) => ({ block: value, params: {} })),
            },
          },
        }));
      },
      setBlockParams: (params: BlockParams, stepIndex: number) => {
        const { pageSlug } = get();
        if (!pageSlug) return;
        set((state) => {
          const page = state.pages[pageSlug] ?? emptyPageState;
          return {
            pages: {
              ...state.pages,
              [pageSlug]: {
                ...page,
                blocks: page.blocks.map((entry: StepperBlock, index: number) =>
                  index === stepIndex ? { ...entry, params } : entry
                ),
              },
            },
          };
        });
      },
      resetPage: (pageSlug?: string) => {
        const slug = pageSlug ?? get().pageSlug;
        if (!slug) return;
        set((state) => ({
          pages: { ...state.pages, [slug]: { ...emptyPageState } },
        }));
      },
      reset: () => set(initialState),
    }),
    {
      name: "dynamic-page-stepper-storage",
      storage: createJSONStorage(() => (isWeb() ? localStorage : AsyncStorage)),
      partialize: (state) => ({
        pageSlug: state.pageSlug,
        pages: Object.fromEntries(
          Object.entries(state.pages).map(([slug, page]) => [
            slug,
            { stepIndex: page.stepIndex, blocks: page.blocks },
          ])
        ),
      }),
    }
  )
);
